import React, { createContext, useContext, useState, useCallback } from "react";
import {
  Box,
  Grid,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useMediaQuery,
} from "@mui/material";
import { Link } from "react-router-dom";

// Remove ToastContext, ToastProvider, useToast, Snackbar, MuiAlert, and related logic

const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    // 1. Payments & Transactions
    {
      question: "Why was my money debited but the payment failed?",
      answer: "This may be due to technical or banking delays. We recommend reaching out to the merchant's support team first. If the issue remains unresolved, you can raise a grievance with us.",
      category: "Payments & Transactions",
      tags: ["payment", "failure", "debit"]
    },
    {
      question: "I completed the payment but the merchant says they haven't received it. What should I do?",
      answer: "We recommend that you first reach out to the merchant's support team to confirm the status of your payment. However, if the merchant is unable to trace the payment or provide a resolution, please raise a grievance with us. Once we receive the details, we will initiate an investigation from our end and assist you further.",
      category: "Payments & Transactions",
      tags: ["payment", "merchant", "grievance"]
    },
    
    // 2. Refunds
    {
      question: "When will I receive my refund?",
      answer: "Refunds are typically initiated by the merchant and are processed within 8-11 working days. Please contact the merchant first to confirm the status of the refund. If they've already processed it and you haven't received the amount, you may raise a ticket with us.",
      category: "Refunds",
      tags: ["refund", "timeline", "merchant"]
    },
    {
      question: "The refund is marked as successful but I haven't received the amount?",
      answer: "We recommend confirming with the merchant whether the refund has been successfully initiated from their end. If the merchant confirms that the refund was processed, please check with your bank to see if the amount has been credited to your account. Refunds usually take 8-11 working days to reflect, depending on your bank's processing timelines. If the refund still doesn't appear in your account after this period, please raise a grievance with us by sharing the necessary details so that we can investigate the issue further and assist you accordingly.",
      category: "Refunds",
      tags: ["refund", "bank", "investigation"]
    },
    
    // 3. Unauthorized / Fraudulent Transactions
    {
      question: "I didn't authorize a transaction. What should I do?",
      answer: "Please confirm with any connected apps or family members. If the transaction is truly unauthorized, report it to the merchant first and ask for the transaction details. If you still feel this transaction should not have been charged to you, raise a grievance with us by sharing the transaction details.",
      category: "Unauthorized / Fraudulent Transactions",
      tags: ["unauthorized", "fraud", "grievance"]
    },
    {
      question: "How do I report a potential fraud or unauthorized transaction to PB Pay?",
      answer: "We recommend contacting the merchant first and reporting a fraud, if no resolution is received. Please contact your bank and raise a dispute against the transaction.",
      category: "Unauthorized / Fraudulent Transactions",
      tags: ["fraud", "dispute", "bank"]
    },
    
    // 4. Merchant/Product Related
    {
      question: "I paid but didn't receive the product/service. What should I do?",
      answer: "Please contact the merchant directly for product or service-related issues. PB Pay helps process the payment - the merchant is responsible for delivering the service/product. Raise a grievance with us only if the merchant is unresponsive.",
      category: "Merchant/Product Related",
      tags: ["product", "service", "merchant"]
    },
    {
      question: "I want to cancel my order and get a refund. Can PB Pay help?",
      answer: "Order cancellation and refunds are managed entirely by the merchant. Please reach out to them to process your request. If the refund is delayed after merchant confirmation, you may contact us with transaction details.",
      category: "Merchant/Product Related",
      tags: ["cancellation", "refund", "merchant"]
    },
    
    // 5. Support & Complaint Resolution
    {
      question: "How long will it take to resolve my complaint?",
      answer: "Resolution timelines may vary depending on the nature of the issue. However, most queries are typically resolved within 10 business days. If you do not receive a resolution within this timeframe, you may escalate the matter as per the escalation matrix outlined in our Grievance Redressal Policy.",
      category: "Support & Complaint Resolution",
      tags: ["resolution", "timeline", "escalation"]
    },
    {
      question: "How will I be updated on my grievance?",
      answer: "Once you raise a complaint with us, you'll receive updates via email at key stages. You can also track the status in the PB Pay grievance dashboard using your reference number by logging in with your email id through which you have registered the grievance.",
      category: "Support & Complaint Resolution",
      tags: ["updates", "email", "dashboard"]
    },
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  return (
    <Box>
      <Box
        sx={{
          px: { xs: "16px", sm: "40px", md: "70px" },
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          marginTop: { xs: "10px", md: "80px" },
        }}
      >
        <Grid
          container
          spacing={2}
          sx={{
            marginBottom: "24px",
            marginLeft: "auto",
            marginRight: "auto",
            marginTop: "45px",
            width: "100%",
            maxWidth: "1280px",
          }}
        >
          <Grid
            item
            xs={12}
            sm={8}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                width: { xs: '100%', sm: '100%', md: '100%', lg: "90%", xl: '80%' },
                alignItems: "center",
                marginBottom: "20px",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <img
                  src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABwAAAAcCAYAAAByDd+UAAAAAXNSR0IArs4c6QAAAr1JREFUSEu9ll1IFFEYht/37PqDN5WilhFKSlnelBkFkVKGFnQRQbJkiZtLGdJl99Nt16GIm0nmD14FgRAWoVEQ6E1iVIuGidGm2UWJxe7OFzux26zuujOTNjAMM2e+9znfOd/PIRxcMojSqBk9mLZrTrsGMoR8wP0GgA53eD/P46sdDfvAwewBUDwGRNhPz8/GTQPKYM5ZgI8QnaZE19S469iwPGIVatlDGdq2BZHwOwEKzTwB5unKLmfDwg8rUOvAga1+gC1JRYlOer61bhhQ+vKrhfqo2bOYeGxlIaqGjQtj6aDreii9RXtB/RQotwDkJexdjPT3uQihBnd4hJ7F96nACUC5X1gApeoA1gqklsCuqF7sinmY7p3AHMCnoDxBRB9hU/CL2QbyYOdRQPkBVKRbEofjU9DRzKa5ccND6S2+KYLbJCACbMYTwA1enr3zB6hBoXT3MwirHXqwvhllDNMzJ6hBj++h+CtyJSM0CUjRBkM/MpR5kL6pJaP+msWld18lIvISZFb8+9poTLRab1zwCyKH6X07mRA0CdB7FReF6IuXrlSCFr4TaKR3qt+snzQPpedAO0Su/9vSSju9r9tWayQHdh7KELceAFHsDMoZhljOaxMhS0Ajcu9WTYCsNNIk1hxSpc2acZnglfGqZJNNWdp0/5EAgDJHHhIB1fJqjy2gdB0LgihwGKRB+l5stwXUu46vAMhOGa2r24V5ZsCKuvo8xzJQNE1J0WhkVaNN0yxMex0lfapxUdN0S0Ej/vpciYSTHI4YFXhIQZ8RWET0PHMOELVG2OXOo++xUV3S52HH6RJR8sHkYQCQboTdPWwb/pxQKDrrdwDwCuAlUBZvyC5XCX3Ds9aAQxcyZWl5QES+K7KbrcNpO7nhcceZkzqkGVBZSgUv2cpDR+lgwcjyIcqClqVf/jvwN9JIKSxcwR3+AAAAAElFTkSuQmCC"
                  alt="FAQ Icon"
                  width={28}
                  height={28}
                />
                <Typography sx={{ fontWeight: 700, fontSize: "24px", lineHeight: "32px" }}>
                  Popular questions
                </Typography>
              </Box>

              <Box sx={{ color: "#0065FF", fontWeight: 500, fontSize: "14px" }}>
                <Link
                  to="/faqs"
                  style={{
                    color: "#0065FF",
                    fontWeight: 500,
                    fontSize: "14px",
                    lineHeight: "16.41px",
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    textDecoration: "none",
                  }}
                >
                  View all FAQs
                </Link>
              </Box>
            </Box>
            {faqs.slice(0, 3).map((faq, index) => (
              <Accordion
                key={index}
                sx={{
                  borderRadius: "8px",
                  boxShadow: "none",
                  marginBottom: isMobile ? 0 : "12px",
                  maxWidth: { xs: '95%', sm: '100%', md: '655px' },
                  width: "100%",
                }}
                expanded={openIndex === index}
                onChange={() => toggleFAQ(index)}
              >
                <AccordionSummary
                  expandIcon={
                    <img
                      src={openIndex === index ? "/Icons/collapse.svg" : "/Icons/Expand.svg"}
                      alt="Expand/Collapse Icon"
                    />
                  }
                  aria-controls={`panel${index}-content`}
                  id={`panel${index}-header`}
                  sx={{
                    border: "1px solid #DFE1E6",
                    borderRadius: "10px",
                    padding: "16px",
                    textAlign: "left",
                    color: "#253858",
                    fontWeight: 700,
                  }}
                >
                  <Typography
                    sx={{
                      width: "100%",
                      paddingLeft: "0px",
                      fontWeight: 700,
                      fontSize: { xs: "14px", md: "16px" },
                      lineHeight: "24px",
                      alignItems: "left",
                    }}
                  >
                    {faq.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    padding: "16px",
                    color: "#253858",
                    fontWeight: 400,
                    fontSize: "16px",
                    lineHeight: "24px",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "14px",
                      color: "#253858",
                      letterSpacing: "0.0014em",
                      lineHeight: "20px",
                      borderRadius: "8px",
                    }}
                  >
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Grid>
          <Grid
            item
            xs={12}
            sm={4}
            sx={{
              display: { xs: "none", sm: 'none', md: "block" },
              textAlign: "center",
              marginTop: { sm: "40px", md: "0" },
            }}
          >
            <img
              src="/Icons/Faq_svg.svg"
              alt="Faq"
              style={{
                maxWidth: "343px",
                height: "auto",
                marginTop: "60px",
              }}
            />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default FAQSection;
